import React, { useEffect, useRef, useState, useCallback } from "react";
import { Text } from "@chakra-ui/react";

/**
 * CountdownTimer
 * @component
 *
 * @param {object}   props
 * @param {number}   props.initialValue        - Starting time in seconds.
 * @param {string}   [props.fontSize="sm"]     - Chakra UI fontSize token (e.g. "sm", "2xl").
 * @param {boolean}  [props.isPlaying=true]    - Pauses/resumes the interval without resetting.
 * @param {boolean}  [props.isRunning=true]    - When false, renders as a static display of initialValue
 * @param {function} [props.onComplete]        - Callback fired once when the countdown reaches 0.

 * @example
 * // Static preview proior to counting down
 * <CountdownTimer initialValue={90} isRunning={false} />
 *
 * @example
 * // Actively counting, turns red in the last 10 seconds
 * <CountdownTimer
 *   initialValue={90}
 *   isRunning={true}
 *   onComplete={() => console.log("Time's up!")}
 * />
 */
const CountdownTimer = ({
  initialValue,
  fontSize = "sm",
  isPlaying = true,
  isRunning = true,
  onComplete,
}) => {
  const [timeRemaining, setTimeRemaining] = useState(initialValue);
  const targetTimeRef = useRef(null);
  const intervalRef = useRef(null);

  // Sync display when not running (static mode)
  useEffect(() => {
    if (!isRunning) {
      setTimeRemaining(initialValue);
      targetTimeRef.current = null;
    }
  }, [initialValue, isRunning]);

  // Establish target anchor when counting starts or initialValue changes mid-run
  useEffect(() => {
    if (!isPlaying || !isRunning) return;

    const newTarget = Date.now() + initialValue * 1000;
    const drift = targetTimeRef.current
      ? Math.abs(targetTimeRef.current - newTarget)
      : Infinity;

    if (drift > 2000) {
      targetTimeRef.current = newTarget;
      setTimeRemaining(initialValue);
    }
  }, [initialValue, isPlaying, isRunning]);

  const tick = useCallback(() => {
    if (!targetTimeRef.current) return;
    const secondsLeft = Math.max(
      0,
      Math.round((targetTimeRef.current - Date.now()) / 1000)
    );
    setTimeRemaining((prev) => {
      if (prev === secondsLeft) return prev;
      if (secondsLeft === 0) onComplete?.();
      return secondsLeft;
    });
  }, [onComplete]);

  // Start / stop the interval
  useEffect(() => {
    if (!isPlaying || !isRunning || !targetTimeRef.current) return;

    intervalRef.current = setInterval(tick, 500);
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, isRunning, tick]);

  // Pause: clear interval without resetting state
  useEffect(() => {
    if (!isPlaying || !isRunning) {
      clearInterval(intervalRef.current);
    }
  }, [isPlaying, isRunning]);

  function formatTimeRemaining(time) {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }

  return <Text fontSize={fontSize}>{formatTimeRemaining(timeRemaining)}</Text>;
};

export default CountdownTimer;
